import { Box, Center, Heading, Stack, Text } from '@chakra-ui/react'
import { faCheck } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import type { FormWizardV2Step } from './types'

type FormWizardV2ChecklistVerticalStatus = 'completed' | 'current' | 'incomplete'

const STATUS_TO_COLORS: Record<
  FormWizardV2ChecklistVerticalStatus,
  {
    textColor: string
    backgroundColor: string
  }
> = {
  completed: {
    backgroundColor: 'primary.muted',
    textColor: 'primary.fg',
  },
  current: {
    backgroundColor: 'primary.solid',
    textColor: 'primary.contrast',
  },
  incomplete: {
    backgroundColor: 'primary.subtle',
    textColor: 'primary.fg',
  },
}

export default function FormWizardV2ChecklistVertical<T extends string>({
  steps,
  currentStep,
}: {
  steps: FormWizardV2Step<T>[]
  currentStep: FormWizardV2Step<T>
}): JSX.Element {
  const visibleSteps = steps.filter(item => item.disabled !== 'hidden')

  const activeStepIndex = visibleSteps.findIndex(step => step.slug === currentStep.slug)

  return (
    <Box position="sticky" width={{ base: '100%', lg: '25%' }}>
      <Text fontWeight={600} color="fg.muted" display={{ base: 'block', lg: 'none' }} mb="8px">
        Step {activeStepIndex + 1} of {visibleSteps.length}{' '}
      </Text>
      <Stack
        direction="column"
        display={{ base: 'none', lg: 'block' }}
        spacing={5}
        top="64px"
        mr="64px"
      >
        {visibleSteps.map((step, index) => {
          if (step.disabled === 'hidden') {
            return null
          }

          let status: FormWizardV2ChecklistVerticalStatus = 'incomplete'
          if (index < activeStepIndex) {
            status = 'completed'
          } else if (index === activeStepIndex) {
            status = 'current'
          }

          return (
            <Stack direction="row" spacing="12px" cursor="default" key={step.slug}>
              <Center
                bg={STATUS_TO_COLORS[status].backgroundColor}
                borderRadius="50%"
                width="42px"
                height="42px"
                fontWeight="bold"
                flexShrink={0}
              >
                <Box fontWeight="bold" color={STATUS_TO_COLORS[status].textColor} fontSize="20px">
                  {status === 'completed' ? (
                    <FontAwesomeIcon icon={faCheck} style={{ marginTop: '10px' }} />
                  ) : (
                    index + 1
                  )}
                </Box>
              </Center>
              <Box>
                <Heading size="lg" as="h4">
                  {step.title}
                </Heading>
              </Box>
            </Stack>
          )
        })}
      </Stack>
    </Box>
  )
}
